(() => {
  'use strict';

  const SELECTOR = '[data-cut-builder-v2]';
  const SMARTCUT_SCRIPT_URL = 'https://cutlistevo.com/checkout/checkout.js';
  const SMARTCUT_STYLE_URL = 'https://cutlistevo.com/checkout/checkout.css';
  let smartCutAssetsPromise = null;
  let activeCalculator = null;

  class SmartCutOriginalCalculatorV2 {
    constructor(builder) {
      this.builder = builder;
      this.sectionId = builder.dataset.sectionId;
      this.productInfo = builder.closest('product-info');
      this.form = document.getElementById(builder.dataset.productFormId);
      this.configElement = document.getElementById(`cut-pricing-config-v2-${this.sectionId}`);
      this.rowsElement = builder.querySelector('[data-cut-rows-v2]');
      this.template = document.getElementById(`CustomCutRowV2-${this.sectionId}`);
      this.messageElement = builder.querySelector('[data-cut-message-v2]');
      this.propertiesContainer = this.form?.querySelector('[data-v2-dynamic-properties]');
      this.materialElement = builder.querySelector('[data-material-total-v2]');
      this.cutChargeElement = builder.querySelector('[data-cut-charge-v2]');
      this.totalElement = builder.querySelector('[data-config-total-v2]');
      this.sheetCountElement = builder.querySelector('[data-sheet-count-v2]');
      this.minimumAdjustmentLabel = builder.querySelector('[data-minimum-adjustment-label-v2]');
      this.minimumAdjustmentElement = builder.querySelector('[data-minimum-adjustment-v2]');
      this.layoutContainer = builder.querySelector('[data-sheet-layouts-v2]');
      this.mainTotalElement = this.productInfo?.querySelector('#totalPriceV2');
      this.widgetHost = builder.querySelector('#smartcut-app');
      this.config = this.readConfig();
      this.lastResult = null;
      this.previewResult = null;
      this.currentVariantAvailable = this.getSelectedVariantData()?.available !== false;
      this.productionPdfBlob = null;
      this.productionPdfUrl = '';
      this.productionPdfFilename = '';
      this.minimumPreviewSequence = 0;
      this.smartcutReady = false;
      this.smartcutLoading = false;
      this.dirty = false;
      this.pendingCalculation = null;
      this.unsubscribeVariant = null;

      if (!this.rowsElement || !this.template || !this.form || !this.configElement || !this.widgetHost) {
        console.error('Plasticare SmartCut original-UI calculator could not initialize because required markup is missing.');
        return;
      }

      this.bindEvents();
      if (!this.rowsElement.querySelector('[data-cut-row-v2]')) this.addRow();
      this.subscribeToVariantChanges();
      this.updateFullSheetState();
      builder.smartCutOriginalCalculatorV2 = this;
      builder.customCutCalculatorV2 = this;
      builder.dataset.initialized = 'true';
    }

    readConfig() {
      const el = this.configElement;
      const number = (value, fallback = 0) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const attr = (name, fallback = 0) => number(el?.getAttribute(name), fallback);
      return {
        maxLength: attr('data-max-l', 96),
        maxWidth: attr('data-max-w', 48),
        bladeWidth: Math.max(0, attr('data-smartcut-blade-width', attr('data-kerf', 0.124))),
        cutPreference: String(el?.getAttribute('data-smartcut-cut-preference') || 'w'),
        beamHeadCuts: String(el?.getAttribute('data-smartcut-beam-head-cuts') || 'false') === 'true',
        minimumTotal: Math.max(0, attr('data-minimum-total', 20)),
        b2bCutFlatRate: Math.max(0, attr('data-b2b-cut-flat-rate', 0)),
        cutRates: {
          1: attr('data-cut-price-1', 10),
          2: attr('data-cut-price-2', 5),
          3: attr('data-cut-price-3', 4),
          4: attr('data-cut-price-4', 3),
          5: attr('data-cut-price-5', 2.5),
          sixToFourteen: attr('data-cut-price-6-plus', 2),
          fifteenPlus: attr('data-cut-price-15-plus', 2),
        },
      };
    }

    bindEvents() {
      this.builder.querySelector('[data-add-cut-v2]')?.addEventListener('click', () => this.addRow());

      this.rowsElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-cut-v2]');
        if (!button) return;
        const row = button.closest('[data-cut-row-v2]');
        if (!row) return;
        if (this.rowsElement.querySelectorAll('[data-cut-row-v2]').length === 1) this.resetRow(row);
        else row.remove();
        this.renumberRows();
        this.markDirty();
      });

      const dirty = () => this.markDirty();
      this.rowsElement.addEventListener('input', dirty);
      this.rowsElement.addEventListener('change', dirty);

      this.form.addEventListener('submit', (event) => {
        if (!this.hasCutIntent()) return;
        if (!this.lastResult || this.dirty) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showMessage('Please open Configure cuts and click Apply Cuts before adding this item to the cart.');
        }
      }, true);

      this.form.addEventListener('formdata', (event) => {
        if (!this.productionPdfBlob || !this.lastResult?.cuts?.length) return;
        const filename = this.productionPdfFilename || 'plasticare-cut-layout.pdf';
        event.formData.set('properties[Production PDF]', this.productionPdfBlob, filename);
      });

      this.cutMinimumCartHandler = null;
      this.productInfo?.querySelector('.qty__input')?.addEventListener('input', () => this.refreshPricePreview());
      this.productInfo?.querySelector('.qty__input')?.addEventListener('change', () => this.refreshPricePreview());

      this.productInfo?.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects') || event.target.closest('[data-cut-builder-v2]')) return;
        window.setTimeout(() => this.handleVariantChange(), 80);
      });
    }

    subscribeToVariantChanges() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId === this.sectionId) {
              if (data?.variant) this.currentVariantAvailable = data.variant.available !== false;
              window.setTimeout(() => this.handleVariantChange(), 0);
            }
          });
        }
      } catch (error) {
        console.warn('Plasticare calculator could not subscribe to variant changes.', error);
      }
    }

    handleVariantChange() {
      this.markDirty();
      if (!this.hasCutIntent()) this.updateFullSheetState();
    }

    markDirty() {
      this.dirty = true;
      this.previewResult = null;
      this.hideMessage();
      this.clearInputErrors();
      if (this.hasCutIntent()) {
        if (this.sheetCountElement) this.sheetCountElement.textContent = 'Calculate layout';
        if (this.layoutContainer) this.layoutContainer.hidden = true;
      } else {
        this.updateFullSheetState();
      }
    }

    addRow(values = {}) {
      const fragment = this.template.content.cloneNode(true);
      const row = fragment.querySelector('[data-cut-row-v2]');
      if (!row) return;
      row.querySelector('[data-cut-width-v2]').value = values.width ?? '';
      row.querySelector('[data-cut-length-v2]').value = values.length ?? '';
      row.querySelector('[data-cut-pieces-v2]').value = values.pieces ?? 1;
      row.querySelector('[data-cut-tolerance-v2]').value = this.normalizeToleranceValue(values.tolerance);
      this.rowsElement.appendChild(fragment);
      this.renumberRows();
      this.dirty = true;
    }

    resetRow(row) {
      row.querySelector('[data-cut-width-v2]').value = '';
      row.querySelector('[data-cut-length-v2]').value = '';
      row.querySelector('[data-cut-pieces-v2]').value = '1';
      row.querySelector('[data-cut-tolerance-v2]').value = '';
      row.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    renumberRows() {
      this.rowsElement.querySelectorAll('[data-cut-row-v2]').forEach((row, index) => {
        row.dataset.cutIndex = String(index + 1);
        row.querySelector('[data-remove-cut-v2]')?.setAttribute('aria-label', `Remove cut ${index + 1}`);
      });
    }

    getRowsSnapshot() {
      return [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')].map((row) => ({
        width: row.querySelector('[data-cut-width-v2]')?.value || '',
        length: row.querySelector('[data-cut-length-v2]')?.value || '',
        pieces: row.querySelector('[data-cut-pieces-v2]')?.value || '1',
        tolerance: row.querySelector('[data-cut-tolerance-v2]')?.value || '',
      }));
    }

    getSnapshot() {
      return {
        rows: this.getRowsSnapshot(),
        lastResult: this.lastResult ? this.clone(this.lastResult) : null,
        dirty: this.dirty,
      };
    }

    restoreSnapshot(snapshot) {
      if (!snapshot) return;
      this.rowsElement.innerHTML = '';
      (snapshot.rows?.length ? snapshot.rows : [{}]).forEach((row) => this.addRow(row));
      this.lastResult = snapshot.lastResult || null;
      this.previewResult = this.lastResult;
      this.dirty = Boolean(snapshot.dirty);
      if (this.lastResult) {
        this.updateDisplay(this.lastResult);
        this.updateProperties(this.lastResult);
        this.renderSheetLayouts(this.lastResult);
      } else if (!this.hasCutIntent()) {
        this.updateFullSheetState();
      } else {
        this.resetCalculatedState(false);
      }
      this.hideMessage();
    }

    clone(value) {
      try { return JSON.parse(JSON.stringify(value)); } catch (_) { return value; }
    }

    clearAll() {
      this.rowsElement.innerHTML = '';
      this.addRow();
      this.lastResult = null;
      this.previewResult = null;
      this.dirty = false;
      this.resetProductionPdf();
      this.clearProperties();
      this.hideMessage();
      if (this.layoutContainer) {
        this.layoutContainer.innerHTML = '';
        this.layoutContainer.hidden = true;
      }
      this.updateFullSheetState();
    }

    hasCutIntent() {
      return [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')].some((row) => {
        const width = String(row.querySelector('[data-cut-width-v2]')?.value || '').trim();
        const length = String(row.querySelector('[data-cut-length-v2]')?.value || '').trim();
        const tolerance = String(row.querySelector('[data-cut-tolerance-v2]')?.value || '').trim();
        const pieces = String(row.querySelector('[data-cut-pieces-v2]')?.value || '').trim();
        return width !== '' || length !== '' || tolerance !== '' || (pieces !== '' && pieces !== '1');
      });
    }

    clearInputErrors() {
      this.rowsElement.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    normalizeToleranceValue(value) {
      const tolerance = String(value || '').trim();
      if (!tolerance) return '';
      if (/exact|standard|1\s*\/\s*16/i.test(tolerance)) return 'Exact';
      return tolerance;
    }

    getToleranceAdjustment(tolerance) {
      // Exact uses the finished dimensions exactly as entered. Nominal +/- 1/8"
      // removes 0.125" from both dimensions before SmartCut optimization.
      return /1\s*\/\s*8/.test(String(tolerance || '')) ? 0.125 : 0;
    }

    readRows({ validate = false, focusError = false } = {}) {
      const sheet = this.getSheetSize();
      const errors = [];
      let firstInvalid = null;
      const cuts = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')].map((row, index) => {
        const widthInput = row.querySelector('[data-cut-width-v2]');
        const lengthInput = row.querySelector('[data-cut-length-v2]');
        const piecesInput = row.querySelector('[data-cut-pieces-v2]');
        const toleranceInput = row.querySelector('[data-cut-tolerance-v2]');
        const width = Number.parseFloat(widthInput?.value);
        const length = Number.parseFloat(lengthInput?.value);
        const pieces = Number.parseInt(piecesInput?.value, 10);
        const tolerance = String(toleranceInput?.value || '').trim();
        const adjustment = this.getToleranceAdjustment(tolerance);
        const packingWidth = width - adjustment;
        const packingLength = length - adjustment;
        [widthInput, lengthInput, piecesInput, toleranceInput].forEach((input) => input?.classList.remove('input-error'));
        const mark = (input, message) => {
          errors.push(`Cut ${index + 1}: ${message}`);
          firstInvalid ||= input;
          if (validate) input?.classList.add('input-error');
        };
        if (!Number.isFinite(width) || width <= 0) mark(widthInput, 'enter a valid width.');
        if (!Number.isFinite(length) || length <= 0) mark(lengthInput, 'enter a valid length.');
        if (!Number.isInteger(pieces) || pieces < 1) mark(piecesInput, 'pieces must be a whole number of 1 or more.');
        if (!tolerance) mark(toleranceInput, 'select a tolerance.');
        if (Number.isFinite(packingWidth) && Number.isFinite(packingLength) && (packingWidth <= 0 || packingLength <= 0)) {
          mark(widthInput, 'the selected tolerance makes the cut dimension zero or less.');
          if (validate) lengthInput?.classList.add('input-error');
        }
        if (Number.isFinite(packingWidth) && Number.isFinite(packingLength) && packingWidth > 0 && packingLength > 0) {
          const long = Math.max(packingWidth, packingLength);
          const short = Math.min(packingWidth, packingLength);
          const sheetLong = Math.max(sheet.width, sheet.length);
          const sheetShort = Math.min(sheet.width, sheet.length);
          if (long > sheetLong + 1e-7 || short > sheetShort + 1e-7) {
            mark(widthInput, `sizes larger than ${this.cleanNumber(sheetShort)} x ${this.cleanNumber(sheetLong)} require a custom quote. Please call 303-781-1171.`);
            if (validate) lengthInput?.classList.add('input-error');
          }
        }
        return { number: index + 1, width, length, pieces, tolerance, toleranceAdjustment: adjustment, packingWidth, packingLength };
      });
      if (errors.length && validate) {
        this.showMessage(errors[0]);
        if (focusError && firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus({ preventScroll: true });
        }
      }
      return { cuts, errors, firstInvalid };
    }

    clearPersistedSmartCutParts() {
      try { localStorage.removeItem('inputs/inputShapes'); } catch (_) {}
      try { localStorage.removeItem('inputs/inputStock'); } catch (_) {}
    }

    async activate() {
      activeCalculator = this;
      if (window.smartcutCheckout?.init) {
        this.smartcutReady = true;
        return true;
      }
      if (this.smartcutLoading) return this.smartcutLoading;
      this.clearPersistedSmartCutParts();
      this.smartcutLoading = this.ensureSmartCutAssets().then(() => this.waitForSmartCutReady());
      try {
        await this.smartcutLoading;
        this.smartcutReady = true;
        return true;
      } finally {
        this.smartcutLoading = false;
      }
    }

    ensureSmartCutAssets() {
      if (smartCutAssetsPromise) return smartCutAssetsPromise;
      smartCutAssetsPromise = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${SMARTCUT_STYLE_URL}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = SMARTCUT_STYLE_URL;
          document.head.appendChild(link);
        }
        if (window.smartcutCheckout?.init) return resolve();
        let script = document.querySelector(`script[src="${SMARTCUT_SCRIPT_URL}"]`);
        if (script) {
          script.addEventListener('load', () => resolve(), { once: true });
          script.addEventListener('error', reject, { once: true });
          return;
        }
        script = document.createElement('script');
        // SmartCut's checkout bundle is an ES module. Loading it as a classic
        // script prevents the module from mounting and no smartcut/ready event is
        // dispatched, which leaves the original Plasticare UI waiting forever.
        script.type = 'module';
        script.src = SMARTCUT_SCRIPT_URL;
        script.dataset.plasticareSmartcutScript = 'true';
        script.addEventListener('load', () => {
          // The ready event is normally emitted during module mount. Keep a
          // cached-load fallback in case the event fires just before this callback.
          window.setTimeout(() => {
            if (window.smartcutCheckout?.init) resolve();
          }, 0);
        }, { once: true });
        script.addEventListener('error', () => {
          smartCutAssetsPromise = null;
          reject(new Error('SmartCut failed to load.'));
        }, { once: true });
        document.head.appendChild(script);
      });
      return smartCutAssetsPromise;
    }

    waitForSmartCutReady() {
      if (window.smartcutCheckout?.init) return Promise.resolve(true);
      return new Promise((resolve, reject) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          window.removeEventListener('smartcut/ready', onReady);
          window.clearTimeout(timer);
          resolve(true);
        };
        const onReady = () => done();
        const timer = window.setTimeout(() => {
          if (window.smartcutCheckout?.init) return done();
          if (settled) return;
          settled = true;
          window.removeEventListener('smartcut/ready', onReady);
          reject(new Error('SmartCut did not become ready.'));
        }, 15000);
        window.addEventListener('smartcut/ready', onReady, { once: true });
      });
    }

    createInitCompleteWaiter(timeoutMs = 10000) {
      let finished = false;
      let timer = null;
      let handler = null;
      const cleanup = () => {
        if (handler) window.removeEventListener('smartcut/initComplete', handler);
        if (timer) window.clearTimeout(timer);
      };
      const promise = new Promise((resolve, reject) => {
        handler = () => {
          if (finished) return;
          finished = true;
          cleanup();
          // Vue updates the input table on the next render tick after initComplete.
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve(true)));
        };
        window.addEventListener('smartcut/initComplete', handler, { once: true });
        timer = window.setTimeout(() => {
          if (finished) return;
          finished = true;
          cleanup();
          reject(new Error('SmartCut initialization timed out. Please try again.'));
        }, timeoutMs);
      });
      return { promise, cancel: cleanup };
    }

    waitForCalculateButton(timeoutMs = 8000) {
      return new Promise((resolve, reject) => {
        const started = Date.now();
        const check = () => {
          const button = this.widgetHost?.querySelector('#calculate-button')
            || document.querySelector('#smartcut-app #smartcut-checkout #calculate-button')
            || document.querySelector('#smartcut-checkout #calculate-button');
          if (button && !button.disabled) return resolve(button);
          if (Date.now() - started >= timeoutMs) {
            return reject(new Error('SmartCut controls did not finish loading. Please close the calculator and try again.'));
          }
          window.setTimeout(check, 50);
        };
        check();
      });
    }

    buildSmartCutConfig(cuts) {
      const sheet = this.getSheetSize();
      const thickness = this.getSelectedThicknessInches();
      const material = String(document.querySelector('h1')?.textContent || 'Plastic Sheet').trim();
      const parts = cuts.map((cut) => ({
        l: cut.packingLength,
        w: cut.packingWidth,
        t: thickness || null,
        q: cut.pieces,
        material,
        name: `Cut ${cut.number}`,
        customData: {
          cutNumber: cut.number,
          originalWidth: cut.width,
          originalLength: cut.length,
          tolerance: cut.tolerance,
          toleranceAdjustment: cut.toleranceAdjustment,
        },
      }));
      return {
        stock: [{
          l: sheet.length,
          w: sheet.width,
          t: thickness || null,
          q: 1000,
          autoAdd: true,
          cost: 1,
          material,
        }],
        parts,
        saw: {
          cutType: 'beam',
          cutPreference: this.config.cutPreference || 'w',
          bladeWidth: this.config.bladeWidth,
          stockType: 'sheet',
          stackHeight: Math.max(1, Math.ceil(thickness || 1)),
          guillotineOptions: { headCuts: Boolean(this.config.beamHeadCuts), strategy: 'efficiency' },
          options: { stackingMode: 'none' },
        },
        options: {
          unitSystem: 'imperial',
          numberFormat: 'decimal',
          decimalPlaces: 3,
          maxParts: 1500,
          enable: {
            diagram: false,
            csvImport: false,
            csvTemplate: false,
            orientation: false,
            partName: true,
            progressNumber: false,
            duplicate: false,
            pagination: false,
          },
        },
      };
    }

    waitForLoadedParts(expected) {
      return new Promise((resolve, reject) => {
        const started = Date.now();
        const check = () => {
          const rows = window.smartcutCheckout?.getInputShapes?.() || [];
          if (rows.length === expected) return resolve(rows);
          if (Date.now() - started > 5000) return reject(new Error('SmartCut did not load the cut rows.'));
          window.setTimeout(check, 40);
        };
        check();
      });
    }

    runSmartCut(cuts) {
      return new Promise(async (resolve, reject) => {
        activeCalculator = this;
        let timeout = null;
        let initWaiter = null;
        let settled = false;

        const onResult = (event) => finish(null, event.detail);
        const onValidation = () => finish(new Error('SmartCut could not calculate this layout. Please review the cut sizes and quantities.'));
        const cleanup = () => {
          window.removeEventListener('smartcut/result', onResult);
          window.removeEventListener('smartcut/validationError', onValidation);
          if (timeout) window.clearTimeout(timeout);
          initWaiter?.cancel?.();
        };
        const finish = (error, value) => {
          if (settled || activeCalculator !== this) return;
          settled = true;
          cleanup();
          error ? reject(error) : resolve(value);
        };

        try {
          await this.activate();
          window.addEventListener('smartcut/result', onResult);
          window.addEventListener('smartcut/validationError', onValidation);
          timeout = window.setTimeout(() => finish(new Error('SmartCut calculation timed out. Please try again.')), 45000);

          // SmartCut exposes its internal inputs before Vue has necessarily
          // rendered the Calculate control. Wait for the documented
          // smartcut/initComplete event, then wait for both the cut rows and
          // the actual Calculate button before triggering the hidden engine.
          initWaiter = this.createInitCompleteWaiter();
          window.smartcutCheckout.init(this.buildSmartCutConfig(cuts));
          await initWaiter.promise;
          await this.waitForLoadedParts(cuts.length);
          const button = await this.waitForCalculateButton();
          button.click();
        } catch (error) {
          finish(error);
        }
      });
    }

    async calculate({ validate = false, focusError = false } = {}) {
      if (this.pendingCalculation) return this.pendingCalculation;
      if (!this.hasCutIntent()) {
        this.clearInputErrors();
        this.hideMessage();
        this.dirty = false;
        this.updateFullSheetState();
        return this.lastResult;
      }

      const { cuts, errors } = this.readRows({ validate, focusError });
      if (errors.length) {
        if (!validate) this.showMessage(errors[0]);
        return null;
      }

      this.pendingCalculation = (async () => {
        this.hideMessage();
        this.setAddToCartEnabled(false);
        try {
          const raw = await this.runSmartCut(cuts);
          const result = this.buildPlasticareResult(raw, cuts);
          if (!result) throw new Error('SmartCut did not return a usable sheet layout.');
          this.lastResult = result;
          this.previewResult = result;
          this.dirty = false;
          this.hideMessage();
          this.updateDisplay(result);
          this.updateProperties(result);
          this.renderSheetLayouts(result);
          this.prepareProductionPdf(result);
          this.setAddToCartEnabled(true);
          return result;
        } catch (error) {
          console.error(error);
          this.showMessage(error?.message || 'Unable to calculate the cut layout. Please try again.');
          this.resetCalculatedState(false);
          this.setAddToCartEnabled(false);
          return null;
        } finally {
          this.pendingCalculation = null;
        }
      })();
      return this.pendingCalculation;
    }

    buildPlasticareResult(rawResult, cuts) {
      const raw = this.getSmartCutV3Result(rawResult);
      const sheet = this.getSheetSize();
      const groups = this.getProductionSheetGroups({ smartcutRaw: rawResult, sheet });
      const sheetCountFromGroups = groups.filter((group) => group.parts.length || group.stock?.used === true).length;
      const stockCost = Number.parseFloat(raw?.metadata?.totalStockCost);
      const sheetCount = Math.max(1, sheetCountFromGroups || (Number.isFinite(stockCost) ? Math.round(stockCost) : 0));
      const totalPieces = cuts.reduce((sum, cut) => sum + cut.pieces, 0);
      const materialPricePerSheet = this.getFullSheetMaterialPrice();
      const materialTotal = materialPricePerSheet * sheetCount;
      // Honor the company's flat cut rate per required sheet, if configured.
      // Otherwise preserve the existing tier-based per-piece pricing.
      const flatRate = this.config.b2bCutFlatRate > 0 ? this.config.b2bCutFlatRate : 0;
      const jobCutRate = this.getCutRate(totalPieces);
      const cutPricingMode = flatRate > 0 ? 'b2b_flat_per_sheet' : 'per_piece';
      const cutCharge = flatRate > 0 ? flatRate * sheetCount : jobCutRate * totalPieces;
      const subtotal = materialTotal + cutCharge;
      const discounted = this.getDiscount(subtotal);
      const preMinimumTotal = discounted.amount;
      const jobId = String(raw?.jobId ?? rawResult?.jobId ?? rawResult?.id ?? 'layout');
      return {
        cuts: cuts.map((cut) => ({
          ...cut,
          cutRate: flatRate > 0 ? 0 : jobCutRate,
          cutCharge: flatRate > 0 ? 0 : jobCutRate * cut.pieces,
          cutPricingMode,
        })),
        totalPieces,
        sheet,
        sheetCount,
        materialPricePerSheet,
        materialTotal,
        cutCharge,
        subtotal,
        discountPercent: discounted.percent,
        preMinimumTotal,
        finalTotal: preMinimumTotal,
        minimumTotal: this.config.minimumTotal,
        cutPricingMode,
        b2bCutFlatRate: flatRate,
        smartcutRaw: rawResult,
        smartcutJobId: jobId,
        smartcutMachine: {
          mode: 'beam', cutPreference: this.config.cutPreference || 'w', bladeWidthIn: this.config.bladeWidth,
          machine: 'Hendrick HM3 Series Horizontal Beam Saw', stackingMode: 'none', headCuts: this.config.beamHeadCuts,
        },
        packing: { fits: true, sheetCount, smartcut: true, cutType: 'beam' },
      };
    }

    getCutRate(pieces) {
      const qty = Number.parseInt(pieces, 10) || 1;
      if (qty >= 15) return this.config.cutRates.fifteenPlus;
      if (qty >= 6) return this.config.cutRates.sixToFourteen;
      return this.config.cutRates[qty] ?? this.config.cutRates.sixToFourteen;
    }

    getSelectedVariantData() {
      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (!selectedVariant?.textContent.trim()) return null;
      try { return JSON.parse(selectedVariant.textContent); } catch (_) { return null; }
    }

    getUnitPrice() {
      const variant = this.getSelectedVariantData();
      return Number.isFinite(variant?.price) ? variant.price / 100 : 0;
    }

    getFullSheetMaterialPrice() { return Math.max(0, this.getUnitPrice()); }

    getOptionName(input) {
      const match = String(input?.getAttribute('name') || '').match(/^options\[(.*)]$/i);
      return match ? match[1].trim() : '';
    }

    getSelectedOptionValue(predicate) {
      for (const input of this.productInfo?.querySelectorAll('variant-selects select[name^="options["]') || []) {
        if (predicate(this.getOptionName(input))) return input.value;
      }
      for (const input of this.productInfo?.querySelectorAll('variant-selects input[name^="options["]:checked') || []) {
        if (predicate(this.getOptionName(input))) return input.value;
      }
      return '';
    }

    parseImperialDimension(value) {
      const text = String(value || '').replace(/[″”]/g, '"').replace(/\b(?:inches|inch|in)\b/gi, '').replace(/"/g, '').trim();
      if (!text) return NaN;
      const mixed = text.match(/(\d+(?:\.\d+)?)\s*[- ]\s*(\d+)\s*\/\s*(\d+)/);
      if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      const fraction = text.match(/^(\d+)\s*\/\s*(\d+)$/);
      if (fraction) return Number(fraction[1]) / Number(fraction[2]);
      const parsed = Number.parseFloat(text);
      return Number.isFinite(parsed) ? parsed : NaN;
    }

    getSelectedThicknessInches() {
      const value = this.getSelectedOptionValue((name) => /thickness/i.test(name));
      const parsed = this.parseImperialDimension(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    parseSheetSize(value) {
      const matches = String(value || '').match(/(\d+(?:\.\d+)?)\s*(?:"|in)?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      if (!matches) return null;
      const a = Number.parseFloat(matches[1]);
      const b = Number.parseFloat(matches[2]);
      if (!(a > 0 && b > 0)) return null;
      return { width: Math.min(a, b), length: Math.max(a, b), label: `${this.cleanNumber(Math.min(a,b))}" x ${this.cleanNumber(Math.max(a,b))}"` };
    }

    getSheetSize() {
      const selected = this.getSelectedOptionValue((name) => /(?:^|\s)(?:sheet\s*)?size(?:\s|$)/i.test(name));
      return this.parseSheetSize(selected) || { width: this.config.maxWidth || 48, length: this.config.maxLength || 96, label: `${this.config.maxWidth || 48}" x ${this.config.maxLength || 96}"` };
    }

    syncB2BDiscountTableForVariant() {
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return;
      const companyId = tableBody.querySelector('.table-price-b2b')?.dataset.companyid || '';
      const variantId = String(this.productInfo?.querySelector('.product-variant-id')?.value || '');
      const entries = [...this.productInfo.querySelectorAll('.bulk_discount_entry')];
      const entry = entries.find((item) => item.dataset.company === companyId && item.dataset.variant === variantId);
      const defaults = {
        1: Number.parseFloat(tableBody.getAttribute('data-def-1-5')) || 0,
        2: Number.parseFloat(tableBody.getAttribute('data-def-6-10')) || 0,
        3: Number.parseFloat(tableBody.getAttribute('data-def-11-20')) || 0,
        4: Number.parseFloat(tableBody.getAttribute('data-def-20-plus')) || 0,
      };
      const discounts = entry ? {
        1: Number.parseFloat(entry.dataset.discount_for_1_to_5) || 0,
        2: Number.parseFloat(entry.dataset.discount_for_6_to_10) || 0,
        3: Number.parseFloat(entry.dataset.discount_for_11_to_20) || 0,
        4: Number.parseFloat(entry.dataset.discount_for_20) || 0,
      } : defaults;
      Object.entries(discounts).forEach(([position, percent]) => {
        const row = tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`);
        const priceCell = row?.querySelector('.table-price-b2b');
        const savingsCell = row?.querySelector('.savings');
        if (priceCell) priceCell.dataset.off = String(percent);
        if (savingsCell) savingsCell.textContent = `(${percent}%)`;
      });
    }

    getDiscount(total) {
      this.syncB2BDiscountTableForVariant();
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return { amount: total, percent: 0 };
      const quantity = Number.parseInt(this.productInfo.querySelector('.qty__input')?.value, 10) || 1;
      let position = 1;
      if (quantity > 5 && quantity <= 10) position = 2;
      else if (quantity > 10 && quantity < 20) position = 3;
      else if (quantity >= 20) position = 4;
      tableBody.querySelectorAll('.discount-table-row').forEach((row) => row.classList.remove('selected'));
      tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`)?.classList.add('selected');
      const cell = tableBody.querySelector(`.discount-table-row[data-pos="${position}"] .table-price-b2b`);
      const percent = Number.parseFloat(cell?.dataset.off) || 0;
      return { amount: total - total * percent / 100, percent };
    }

    updateFullSheetState() {
      if (this.hasCutIntent()) return;
      const price = this.getDiscount(this.getFullSheetMaterialPrice()).amount;
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(this.getFullSheetMaterialPrice());
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(0);
      if (this.sheetCountElement) this.sheetCountElement.textContent = '1 full sheet';
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(price);
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(price);
        this.mainTotalElement.dataset.total = price.toFixed(2);
      }
      this.lastResult = null;
      this.previewResult = null;
      this.clearProperties();
      this.setAddToCartEnabled(true);
      if (this.layoutContainer) this.layoutContainer.hidden = true;
    }

    resetCalculatedState(clearProperties = true) {
      this.lastResult = null;
      this.previewResult = null;
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(0);
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(0);
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(0);
      if (this.sheetCountElement) this.sheetCountElement.textContent = '—';
      if (this.layoutContainer) this.layoutContainer.hidden = true;
      if (clearProperties) this.clearProperties();
    }

    updateDisplay(result) {
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(result.materialTotal);
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(result.cutCharge);
      if (this.sheetCountElement) this.sheetCountElement.textContent = `${result.sheetCount} full sheet${result.sheetCount === 1 ? '' : 's'}`;
      if (this.minimumAdjustmentLabel) this.minimumAdjustmentLabel.hidden = true;
      if (this.minimumAdjustmentElement) this.minimumAdjustmentElement.hidden = true;
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(result.preMinimumTotal);
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(result.preMinimumTotal);
        this.mainTotalElement.dataset.total = result.preMinimumTotal.toFixed(2);
      }
      this.refreshPricePreview(result);
    }

    refreshPricePreview(result = this.lastResult) {
      if (!result?.cuts?.length) return;
      const unitTotal = Number(result.preMinimumTotal || 0);
      const productQty = Math.max(1, Number.parseInt(this.productInfo?.querySelector('.qty__input')?.value, 10) || 1);
      const displayLineTotal = unitTotal * productQty;
      if (this.minimumAdjustmentLabel) this.minimumAdjustmentLabel.hidden = true;
      if (this.minimumAdjustmentElement) this.minimumAdjustmentElement.hidden = true;
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(displayLineTotal);
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(displayLineTotal);
        this.mainTotalElement.dataset.total = displayLineTotal.toFixed(2);
      }
    }

    renderSheetLayouts(result) {
      if (!this.layoutContainer) return;
      this.layoutContainer.innerHTML = '';
      const groups = this.getProductionSheetGroups(result);
      if (!groups.length) {
        this.layoutContainer.hidden = true;
        return;
      }
      const heading = document.createElement('div');
      heading.className = 'custom-cut-layout-v2__heading';
      const copy = document.createElement('div');
      const strong = document.createElement('strong');
      const note = document.createElement('span');
      strong.textContent = `${groups.length} full sheet${groups.length === 1 ? '' : 's'} required`;
      note.textContent = `SmartCut beam-saw layout • ${this.cleanNumber(this.config.bladeWidth)}" blade kerf included`;
      copy.append(strong, note);
      const download = document.createElement('a');
      download.className = 'custom-cut-layout-v2__download';
      download.textContent = 'Download production PDF';
      download.href = '#';
      download.addEventListener('click', async (event) => {
        event.preventDefault();
        if (!this.productionPdfBlob) this.prepareProductionPdf(result);
        if (!this.productionPdfBlob) return;
        const link = document.createElement('a');
        link.href = this.productionPdfUrl;
        link.download = this.productionPdfFilename || 'plasticare-cut-layout.pdf';
        link.click();
      });
      heading.append(copy, download);
      this.layoutContainer.appendChild(heading);
      const grid = document.createElement('div');
      grid.className = 'custom-cut-layout-v2__grid';
      groups.forEach((group, index) => grid.appendChild(this.createSheetLayoutCard(result, group, index)));
      this.layoutContainer.appendChild(grid);
      this.layoutContainer.hidden = false;
    }

    createSheetLayoutCard(result, group, index) {
      const card = document.createElement('section');
      card.className = 'custom-cut-layout-v2__card';
      const head = document.createElement('div');
      head.className = 'custom-cut-layout-v2__card-header';
      const title = document.createElement('strong');
      const meta = document.createElement('span');
      title.textContent = `Sheet ${index + 1}`;
      meta.textContent = `${group.parts.length} pieces • ${result.sheet.label}`;
      head.append(title, meta);
      card.appendChild(head);
      const ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg');
      const stockL = Number(group.stock?.l) || result.sheet.length;
      const stockW = Number(group.stock?.w) || result.sheet.width;
      svg.setAttribute('viewBox', `0 0 ${stockL} ${stockW}`);
      svg.classList.add('custom-cut-layout-v2__svg');
      const border = document.createElementNS(ns, 'rect');
      border.setAttribute('x', '0'); border.setAttribute('y', '0'); border.setAttribute('width', stockL); border.setAttribute('height', stockW);
      border.setAttribute('class', 'custom-cut-layout-v2__sheet-outline');
      svg.appendChild(border);
      group.parts.forEach((part) => {
        const l = Number(part.l) || 0;
        const w = Number(part.w) || 0;
        const x = Number(part.x) || 0;
        const y = stockW - (Number(part.y || 0) + w);
        const rect = document.createElementNS(ns, 'rect');
        rect.setAttribute('x', x); rect.setAttribute('y', y); rect.setAttribute('width', Math.max(.001,l)); rect.setAttribute('height', Math.max(.001,w));
        rect.setAttribute('class', 'custom-cut-layout-v2__piece');
        const t = document.createElementNS(ns, 'title');
        t.textContent = `${part.name || 'Piece'}: ${this.cleanNumber(l)}" x ${this.cleanNumber(w)}"`;
        rect.appendChild(t); svg.appendChild(rect);
        if (l >= 8 && w >= 4) {
          const label = document.createElementNS(ns, 'text');
          label.setAttribute('x', x + l/2); label.setAttribute('y', y + w/2); label.setAttribute('class', 'custom-cut-layout-v2__piece-label');
          label.textContent = part?.customData?.cutNumber ? `C${part.customData.cutNumber}` : (part.name || 'Cut');
          svg.appendChild(label);
        }
      });
      card.appendChild(svg);
      return card;
    }

    resetProductionPdf() {
      if (this.productionPdfUrl) URL.revokeObjectURL(this.productionPdfUrl);
      this.productionPdfBlob = null;
      this.productionPdfUrl = '';
      this.productionPdfFilename = '';
    }

    prepareProductionPdf(result) {
      try {
        const blob = this.buildCutListStylePdf(result);
        if (!(blob instanceof Blob) || blob.size < 100) return null;
        this.resetProductionPdf();
        this.productionPdfBlob = blob;
        this.productionPdfFilename = `plasticare-cut-layout-${String(result.smartcutJobId || 'layout').replace(/[^a-zA-Z0-9_-]+/g, '-')}.pdf`;
        this.productionPdfUrl = URL.createObjectURL(blob);
        return blob;
      } catch (error) {
        console.error('Unable to build production PDF.', error);
        return null;
      }
    }

    updateProperties(result) {
      const set = (selector, value) => {
        const input = this.form.querySelector(selector);
        if (!input) return;
        input.disabled = false;
        input.value = value;
      };
      const details = result.cuts.map((cut) => `${this.cleanNumber(cut.width)}" × ${this.cleanNumber(cut.length)}" × ${cut.pieces} (${cut.tolerance})`).join('; ');
      const data = result.cuts.map((cut) => ({
        number: cut.number, width: cut.width, length: cut.length, pieces: cut.pieces, tolerance: cut.tolerance,
        toleranceAdjustment: cut.toleranceAdjustment, packingWidth: cut.packingWidth, packingLength: cut.packingLength,
      }));
      set('[data-v2-prop-total]', Number(result.preMinimumTotal || 0).toFixed(4));
      set('[data-v2-prop-cut-charge]', Number(result.cutCharge || 0).toFixed(2));
      set('[data-v2-prop-cut-data]', JSON.stringify(data));
      set('[data-v2-prop-material-price]', Number(result.materialTotal || 0).toFixed(2));
      if (!this.propertiesContainer) return;
      this.propertiesContainer.innerHTML = '';
      const add = (name, value) => {
        if (value == null || String(value).trim() === '') return;
        const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.value = String(value); this.propertiesContainer.appendChild(input);
      };
      add('Cut Summary', details);
      add('Sheets Required', String(result.sheetCount));
      add('_materialPricePerSheet', Number(result.materialPricePerSheet || 0).toFixed(2));
      add('_smartcutJobId', result.smartcutJobId);
      add('_smartcutEnabled', 'true');
      // Full-sheet cut service is not part of the shared cut-to-size minimum.
      add('_smartcutMeta', JSON.stringify({ machine:'Hendrick HM3 Series Horizontal Beam Saw', mode:'beam', cutPreference:this.config.cutPreference || 'w', stacking:'single_sheet', bladeKerfIn:this.config.bladeWidth }));
      if (result.cutPricingMode === 'b2b_flat_per_sheet' && result.b2bCutFlatRate > 0) {
        add('_cutPricingMode', 'b2b_flat_per_sheet');
        add('_b2bCutFlatRatePerSheet', Number(result.b2bCutFlatRate).toFixed(2));
      }
    }

    clearProperties() {
      ['[data-v2-prop-total]','[data-v2-prop-cut-charge]','[data-v2-prop-cut-data]','[data-v2-prop-material-price]']
        .forEach((selector) => {
          const input = this.form.querySelector(selector);
          if (!input) return;
          input.value = '';
          input.disabled = true;
        });
      if (this.propertiesContainer) this.propertiesContainer.innerHTML = '';
    }

    setAddToCartEnabled(enabled) {
      const button = this.form.querySelector('button[type="submit"][name="add"]');
      if (!button) return;
      if (!enabled) {
        button.dataset.cutValidationBlocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }
      if (button.dataset.cutValidationBlocked !== 'true') return;
      delete button.dataset.cutValidationBlocked;
      const variantInput = this.form.querySelector('.product-variant-id');
      if (this.currentVariantAvailable !== false && variantInput?.value && !variantInput.disabled) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    showMessage(message) {
      if (!this.messageElement) return;
      this.messageElement.textContent = message;
      this.messageElement.hidden = false;
    }

    hideMessage() {
      if (!this.messageElement) return;
      this.messageElement.textContent = '';
      this.messageElement.hidden = true;
    }

    cleanNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? String(Number(number.toFixed(3))) : '';
    }

    formatMoney(amount) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const format = window.theme?.moneyFormat || '${{amount}}';
          return Shopify.formatMoney(Math.round((amount || 0) * 100), format);
        }
      } catch (_) {}
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style:'currency', currency:window.Shopify?.currency?.active || 'USD' }).format(amount || 0);
    }

    getSmartCutV3Result(source) {
      const raw = source?.smartcutRaw || source || {};
      const candidates = [
        raw?.apiResultV3,
        raw?.result?.apiResultV3,
        raw?.optimisation?.apiResultV3,
        raw?.optimization?.apiResultV3,
        raw?.data?.apiResultV3,
        raw?.result,
        raw,
      ];

      return candidates.find((candidate) => candidate && typeof candidate === 'object'
        && (Array.isArray(candidate.stock) || Array.isArray(candidate.parts) || Array.isArray(candidate.cuts))) || raw;
    }

    getProductionSheetGroups(result) {
      const raw = this.getSmartCutV3Result(result);
      const allStocks = Array.isArray(raw.stock) ? raw.stock.filter(Boolean) : [];
      const placedParts = Array.isArray(raw.parts)
        ? raw.parts.filter((part) => part
          && part.x != null && part.y != null
          && Number.isFinite(Number(part.x)) && Number.isFinite(Number(part.y)))
        : [];
      const cuts = Array.isArray(raw.cuts) ? raw.cuts.filter(Boolean) : [];
      const offcuts = Array.isArray(raw.offcuts) ? raw.offcuts.filter(Boolean) : [];

      const getRefId = (item) => {
        const value = item?.stock?.id ?? item?.stockId ?? item?.stock_id ?? '';
        return value == null ? '' : String(value);
      };
      const getStockId = (stock, index) => {
        const value = stock?.id ?? stock?.autoId ?? stock?.stockId ?? stock?.stock_id ?? index;
        return value == null ? String(index) : String(value);
      };

      // A SmartCut V3 result may contain available stock as well as used stock.
      // Keep a row when SmartCut marks it used OR a placed item/cut/offcut references it.
      const referencedIds = new Set([
        ...placedParts.map(getRefId),
        ...cuts.map(getRefId),
        ...offcuts.map(getRefId),
      ].filter(Boolean));
      let stocks = allStocks.filter((stock, index) => stock?.used === true || referencedIds.has(getStockId(stock, index)));
      if (!stocks.length) stocks = allStocks.filter((stock) => stock?.used !== false);

      const groups = stocks.map((stock, index) => {
        const stockId = getStockId(stock, index);
        return {
          stock,
          stockId,
          parts: placedParts.filter((part) => getRefId(part) === stockId),
          cuts: cuts.filter((cut) => getRefId(cut) === stockId),
          offcuts: offcuts.filter((offcut) => getRefId(offcut) === stockId),
          index,
        };
      });

      // Some SmartCut result variants omit the stock reference on child rows when
      // there is only one physical sheet. In that case the association is unambiguous.
      if (groups.length === 1) {
        if (!groups[0].parts.length && placedParts.length) groups[0].parts = placedParts;
        if (!groups[0].cuts.length && cuts.length) groups[0].cuts = cuts;
        if (!groups[0].offcuts.length && offcuts.length) groups[0].offcuts = offcuts;
      }

      if (groups.length) return groups;

      // Last-resort fallback if the widget result omits stock rows entirely.
      const sheet = result?.sheet || this.getSheetSize();
      return [{
        stock: { id: 'sheet-1', l: sheet.length, w: sheet.width, used: true },
        stockId: 'sheet-1', parts: placedParts, cuts, offcuts, index: 0,
      }];
    }

    getPartSizeKey(part) {
      const l = Number(part?.l);
      const w = Number(part?.w);
      const a = Number.isFinite(l) ? l : 0;
      const b = Number.isFinite(w) ? w : 0;
      const long = Math.max(a, b);
      const short = Math.min(a, b);
      return `${this.cleanNumber(short)}x${this.cleanNumber(long)}`;
    }

    getPdfPaletteColor(index) {
      const palette = [
        [0.83, 0.91, 0.89], // mint
        [0.96, 0.86, 0.86], // blush
        [0.91, 0.88, 0.95], // lilac
        [0.97, 0.93, 0.77], // sand
        [0.84, 0.91, 0.96], // blue
        [0.91, 0.94, 0.84], // green
        [0.96, 0.89, 0.80], // peach
        [0.88, 0.88, 0.92], // grey violet
      ];
      return palette[index % palette.length];
    }

    buildCutListStylePdf(result) {
      const groups = this.getProductionSheetGroups(result);
      const raw = this.getSmartCutV3Result(result);

      // Match the customer's CutList Optimizer reference: A4 portrait, narrow
      // left production tables, large right sheet diagram, and flowing two-sheet pages.
      const pageW = 595.28;
      const pageH = 841.89;
      const margin = 25.2;
      const footerY = 17.0;
      const titleY = 817.4;
      const titleLineY = 810.7;
      const firstPageFirstBlockTop = 722.7;
      const laterPageFirstBlockTop = 785.5;
      const interBlockGap = 27.6;
      const footerSafeTop = 45;
      const fontRegular = 3;
      const fontBold = 4;
      const pdfObjects = {};
      const pageStreams = [];

      const escapePdf = (value) => String(value ?? '')
        .replace(/[^\x20-\x7E]/g, ' ')
        .replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
      const num = (value) => Number(value || 0).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
      const clean = (value) => this.cleanNumber(Number(value) || 0);
      const date = new Date();
      const dateText = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

      const line = (cmds, x1, y1, x2, y2, width = .5, rgb = [0,0,0]) => {
        cmds.push(`${rgb.join(' ')} RG ${width} w ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S`);
      };
      const rect = (cmds, x, y, w, h, fill = null, stroke = [0,0,0], sw = .5) => {
        if (fill) cmds.push(`${fill.join(' ')} rg`);
        if (stroke) cmds.push(`${stroke.join(' ')} RG ${sw} w`);
        cmds.push(`${num(x)} ${num(y)} ${num(w)} ${num(h)} re ${fill && stroke ? 'B' : fill ? 'f' : 'S'}`);
      };
      const text = (cmds, value, x, y, size = 7, bold = false, rgb = [0.15,0.15,0.15]) => {
        if (value == null || value === '') return;
        cmds.push(`${rgb.join(' ')} rg BT /F${bold ? 2 : 1} ${size} Tf ${num(x)} ${num(y)} Td (${escapePdf(value)}) Tj ET`);
      };
      const textRot90 = (cmds, value, x, y, size = 6, bold = false, rgb = [0.25,0.25,0.25]) => {
        if (value == null || value === '') return;
        cmds.push(`${rgb.join(' ')} rg BT /F${bold ? 2 : 1} ${size} Tf 0 1 -1 0 ${num(x)} ${num(y)} Tm (${escapePdf(value)}) Tj ET`);
      };
      const fitText = (cmds, value, x, y, maxChars, size = 7, bold = false, rgb) => {
        let str = String(value ?? '');
        if (str.length > maxChars) str = str.slice(0, Math.max(0, maxChars - 3)) + '...';
        text(cmds, str, x, y, size, bold, rgb);
      };
      const aggregateSizes = (parts) => {
        const map = new Map();
        parts.forEach((part) => {
          const key = this.getPartSizeKey(part);
          const q = Math.max(1, Number(part?.q) || 1);
          map.set(key, (map.get(key) || 0) + q);
        });
        return [...map.entries()];
      };
      const compactTolerance = (value) => {
        const tolerance = String(value || '').trim();
        if (/1\s*\/\s*8/.test(tolerance)) return 'Nominal +/- 1/8"';
        if (/exact|1\s*\/\s*16/i.test(tolerance)) return 'Exact';
        return tolerance || 'Not specified';
      };
      const resolvePartTolerance = (part) => {
        const direct = String(part?.customData?.tolerance || part?.tolerance || '').trim();
        if (direct) return direct;
        const partL = Number(part?.l || 0);
        const partW = Number(part?.w || 0);
        const partName = String(part?.name || '').trim().toLowerCase();
        const match = (result.cuts || []).find((cut) => {
          const cutL = Number(cut?.packingLength ?? cut?.length ?? 0);
          const cutW = Number(cut?.packingWidth ?? cut?.width ?? 0);
          const cutName = String(cut?.name || '').trim().toLowerCase();
          const sameName = !partName || !cutName || partName === cutName;
          const directDims = Math.abs(partL - cutL) < 0.02 && Math.abs(partW - cutW) < 0.02;
          const rotatedDims = Math.abs(partL - cutW) < 0.02 && Math.abs(partW - cutL) < 0.02;
          return sameName && (directDims || rotatedDims);
        });
        return String(match?.tolerance || '').trim();
      };
      const aggregateSizeToleranceRows = (parts) => {
        const map = new Map();
        parts.forEach((part) => {
          const size = this.getPartSizeKey(part);
          const tolerance = resolvePartTolerance(part);
          const compact = compactTolerance(tolerance);
          const q = Math.max(1, Number(part?.q) || 1);
          const key = `${size}|||${compact}`;
          const current = map.get(key) || { size, tolerance: compact, qty: 0 };
          current.qty += q;
          map.set(key, current);
        });
        return [...map.values()];
      };

      // Same size = same pastel colour, as in the reference report.
      const sizeColorMap = new Map();
      const allPlacedParts = groups.flatMap((group) => group.parts || []);
      aggregateSizes(allPlacedParts).forEach(([key], index) => sizeColorMap.set(key, this.getPdfPaletteColor(index)));

      const buildGuillotineCutRows = (group, stockL, stockW) => {
        const epsilon = 0.08;
        const partRects = (group.parts || []).map((part) => ({
          x: Number(part?.x || 0),
          y: Number(part?.y || 0),
          // SmartCut V3 already returns l/w in the FINAL placed orientation.
          // `rot` records how the original input was rotated; swapping here again
          // would rotate the PDF geometry a second time and make it disagree with
          // SmartCut's on-screen diagram.
          w: Number(part?.l || 0),
          h: Number(part?.w || 0),
          label: this.getPartSizeKey(part).replace('x', ' x '),
        })).filter((part) => part.w > 0 && part.h > 0);

        const hasPlacedPart = (panel) => partRects.some((part) => {
          const cx = part.x + part.w / 2;
          const cy = part.y + part.h / 2;
          return cx >= panel.x - epsilon && cx <= panel.x + panel.w + epsilon
            && cy >= panel.y - epsilon && cy <= panel.y + panel.h + epsilon;
        });
        const exactPart = (panel) => partRects.find((part) =>
          Math.abs(part.x - panel.x) <= epsilon
          && Math.abs(part.y - panel.y) <= epsilon
          && Math.abs(part.w - panel.w) <= epsilon
          && Math.abs(part.h - panel.h) <= epsilon
        );
        const childResult = (panel) => {
          if (!panel || panel.w <= epsilon || panel.h <= epsilon) return '-';
          const exact = exactPart(panel);
          if (exact) return exact.label;
          return hasPlacedPart(panel) ? '-' : 'surplus';
        };

        const active = [{ x: 0, y: 0, w: stockL, h: stockW }];
        const orderedCuts = [...(group.cuts || [])].sort((a,b)=>(Number(a.order)||9999)-(Number(b.order)||9999));
        return orderedCuts.map((cut, index) => {
          const x1 = Number(cut.x1 || 0), x2 = Number(cut.x2 || 0);
          const y1 = Number(cut.y1 || 0), y2 = Number(cut.y2 || 0);
          const vertical = Math.abs(x2 - x1) <= Math.abs(y2 - y1);
          const coordinate = vertical ? (x1 + x2) / 2 : (y1 + y2) / 2;
          const segMin = vertical ? Math.min(y1,y2) : Math.min(x1,x2);
          const segMax = vertical ? Math.max(y1,y2) : Math.max(x1,x2);
          const candidates = active.filter((panel) => {
            if (vertical) {
              return coordinate > panel.x + epsilon && coordinate < panel.x + panel.w - epsilon
                && segMin >= panel.y - epsilon && segMax <= panel.y + panel.h + epsilon;
            }
            return coordinate > panel.y + epsilon && coordinate < panel.y + panel.h - epsilon
              && segMin >= panel.x - epsilon && segMax <= panel.x + panel.w + epsilon;
          }).sort((a,b)=>(a.w*a.h)-(b.w*b.h));
          const panel = candidates[0] || active.slice().sort((a,b)=>(a.w*a.h)-(b.w*b.h))[0] || {x:0,y:0,w:stockL,h:stockW};
          const panelLabel = `${clean(panel.h)} x ${clean(panel.w)}`;
          let first, second;
          if (vertical) {
            first = { x: panel.x, y: panel.y, w: Math.max(0, coordinate - panel.x), h: panel.h };
            second = { x: coordinate, y: panel.y, w: Math.max(0, panel.x + panel.w - coordinate), h: panel.h };
          } else {
            first = { x: panel.x, y: panel.y, w: panel.w, h: Math.max(0, coordinate - panel.y) };
            second = { x: panel.x, y: coordinate, w: panel.w, h: Math.max(0, panel.y + panel.h - coordinate) };
          }
          const panelIndex = active.indexOf(panel);
          if (panelIndex >= 0) active.splice(panelIndex, 1, ...[first,second].filter((entry)=>entry.w > epsilon && entry.h > epsilon));
          return {
            number: index + 1,
            panel: panelLabel,
            cut: `${vertical ? 'x' : 'y'}=${num(coordinate)}`,
            result: `${childResult(first)} | ${childResult(second)}`,
          };
        });
      };

      const metadata = raw?.metadata || {};
      const calculatedStockArea = groups.reduce((sum, group) => sum + Number(group.stock?.l || result.sheet.length) * Number(group.stock?.w || result.sheet.width), 0);
      const calculatedUsedArea = groups.reduce((sum, group) => sum + (group.parts || []).reduce((partSum, part) => partSum + Number(part.l || 0) * Number(part.w || 0) * Math.max(1, Number(part.q) || 1), 0), 0);
      const metaStockArea = Number(metadata.totalStockArea);
      const metaUsedArea = Number(metadata.totalStockUsedArea ?? metadata.totalPartsArea);
      const metaWasteArea = Number(metadata.totalStockUnusedArea);
      const overallStockArea = Number.isFinite(metaStockArea) && metaStockArea > 0 ? metaStockArea : calculatedStockArea;
      const overallUsedArea = Number.isFinite(metaUsedArea) && metaUsedArea > 0 ? metaUsedArea : calculatedUsedArea;
      const overallWaste = Number.isFinite(metaWasteArea) && metaWasteArea >= 0 ? metaWasteArea : Math.max(0, overallStockArea - overallUsedArea);
      const overallUsedPct = overallStockArea > 0 ? overallUsedArea / overallStockArea * 100 : 0;
      const overallWastePct = overallStockArea > 0 ? overallWaste / overallStockArea * 100 : 0;
      const metaCuts = Number(metadata.totalCuts);
      const overallCuts = Number.isFinite(metaCuts) && metaCuts >= 0 ? metaCuts : (Array.isArray(raw.cuts) ? raw.cuts.length : 0);
      const metaCutLength = Number(metadata.totalCutLength);
      const overallCutLength = Number.isFinite(metaCutLength) && metaCutLength >= 0
        ? metaCutLength
        : (Array.isArray(raw.cuts) ? raw.cuts.reduce((sum, cut) => sum + Math.hypot(Number(cut.x2 || 0) - Number(cut.x1 || 0), Number(cut.y2 || 0) - Number(cut.y1 || 0)), 0) : 0);
      const overallSizes = aggregateSizes(allPlacedParts);
      const overallToleranceLabels = [...new Set((result.cuts || [])
        .map((cut) => String(cut?.tolerance || '').trim())
        .filter(Boolean)
        .map(compactTolerance))];
      const overallPieceSummary = (result.cuts || []).map((cut) => {
        const qty = Math.max(1, Number(cut?.pieces) || 1);
        const width = clean(cut?.width);
        const length = clean(cut?.length);
        const tolerance = compactTolerance(cut?.tolerance);
        return `${qty} ${qty === 1 ? 'piece' : 'pieces'} ${width}" x ${length}" (${tolerance})`;
      });

      const getGroupMetrics = (group) => {
        const stockL = Number(group.stock?.l || result.sheet.length || 96);
        const stockW = Number(group.stock?.w || result.sheet.width || 48);
        const sizes = aggregateSizeToleranceRows(group.parts || []);
        const cutRows = buildGuillotineCutRows(group, stockL, stockW);
        // Measured from the supplied CutList Optimizer PDF: 9.6pt row pitch,
        // panel table starts 78.5pt below the sheet top, cut table follows it.
        const cutHeaderOffset = 78.5 + ((sizes.length + 1) * 9.6) + 8.5;
        const leftTableHeight = cutHeaderOffset + (cutRows.length * 9.6) + 6;
        const layoutHeight = Math.min(187.8, (stockW / Math.max(stockL, .001)) * 375.6);
        return { stockL, stockW, sizes, cutRows, cutHeaderOffset, leftTableHeight, layoutHeight, blockHeight: Math.max(leftTableHeight, layoutHeight) };
      };

      // Flow sheet blocks exactly like the reference: summary on page 1, title only
      // on later pages, and the next sheet begins after the left cut table finishes.
      const plannedPages = [];
      let currentPage = { first: true, blocks: [] };
      let nextTop = firstPageFirstBlockTop;
      groups.forEach((group) => {
        const metrics = getGroupMetrics(group);
        if (currentPage.blocks.length && nextTop - metrics.blockHeight < footerSafeTop) {
          plannedPages.push(currentPage);
          currentPage = { first: false, blocks: [] };
          nextTop = laterPageFirstBlockTop;
        }
        currentPage.blocks.push({ group, top: nextTop, metrics });
        nextTop -= metrics.blockHeight + interBlockGap;
      });
      if (currentPage.blocks.length || !plannedPages.length) plannedPages.push(currentPage);
      const pageCount = plannedPages.length;

      const drawPageTitle = (cmds) => {
        text(cmds, 'CutList Optimizer', margin, titleY, 8.4, false, [0.18,0.18,0.18]);
        line(cmds, margin, titleLineY, pageW - margin, titleLineY, .55, [0.20,0.20,0.20]);
      };

      const drawOverallSummary = (cmds) => {
        drawPageTitle(cmds);
        const leftX = margin;
        const valX = 118.8;
        const rightX = 243.6;
        const rightValX = 293.2;
        const rowStartY = 800.5;
        const rowStep = 9.8;
        const rows = [
          ['Used stock sheets', String(groups.length)],
          ['Total used area', `${num(overallUsedArea)} ${num(overallUsedPct)}%`],
          ['Total wasted area', `${num(overallWaste)} ${num(overallWastePct)}%`],
          ['Total cuts', String(overallCuts)],
          ['Total cut length', num(overallCutLength)],
          ['Cut / blade / kerf thickness', `${clean(this.config.bladeWidth)}`],
        ];
        rows.forEach((row, i) => {
          const y = rowStartY - i * rowStep;
          text(cmds, row[0], leftX, y, 5.5, true, [0.22,0.22,0.22]);
          text(cmds, row[1], valX, y, 5.5, false, [0.22,0.22,0.22]);
        });
        // Faint separator between left labels and values, as in the reference.
        line(cmds, 115.0, 803.8, 115.0, 747.0, .25, [0.75,0.75,0.75]);

        text(cmds, 'Total Pieces', rightX, rowStartY, 5.5, true);
        const panelText = overallPieceSummary.length
          ? overallPieceSummary.join('  |  ')
          : overallSizes.map(([key, qty]) => `${qty} ${qty === 1 ? 'piece' : 'pieces'} ${key.replace('x','\" x ')}\"`).join('  |  ');
        fitText(cmds, panelText, rightValX, rowStartY, 104, 4.9, false);
        text(cmds, 'Stock sheets', rightX, rowStartY - rowStep, 5.5, true);
        text(cmds, `${clean(result.sheet.width)} x ${clean(result.sheet.length)} x${groups.length}`, rightValX, rowStartY - rowStep, 5.5, false);
        text(cmds, 'Total cut pieces', rightX, rowStartY - rowStep * 2, 5.5, true);
        text(cmds, String(result.totalPieces || allPlacedParts.reduce((sum,p)=>sum+Math.max(1,Number(p.q)||1),0)), rightValX, rowStartY - rowStep * 2, 5.5, false);
        text(cmds, 'Machine', rightX, rowStartY - rowStep * 3, 5.5, true);
        fitText(cmds, 'Hendrick HM3 - beam / width priority', rightValX, rowStartY - rowStep * 3, 48, 5.25, false);
        text(cmds, 'Tolerance', rightX, rowStartY - rowStep * 4, 5.5, true);
        fitText(cmds, overallToleranceLabels.length ? overallToleranceLabels.join(' | ') : 'Not specified', rightValX, rowStartY - rowStep * 4, 58, 5.0, false);
        line(cmds, margin, 741.5, pageW - margin, 741.5, .4, [0.28,0.28,0.28]);
      };

      const drawSheetBlock = (cmds, entry) => {
        const { group, top: blockTop, metrics } = entry;
        const { stockL, stockW, sizes, cutRows } = metrics;
        const leftX = margin;
        const leftW = 120.4;
        const layoutX = 172.0;
        const layoutMaxW = 375.6;
        const layoutMaxH = 187.8;
        const scale = Math.min(layoutMaxW / Math.max(stockL,.001), layoutMaxH / Math.max(stockW,.001));
        const layoutW = stockL * scale;
        const layoutH = stockW * scale;
        const layoutY = blockTop - layoutH;
        const stockArea = stockL * stockW;
        const analysis = group.stock?.analysis || {};
        const calculatedSheetUsedArea = (group.parts || []).reduce((sum,part)=>sum + Number(part.l||0)*Number(part.w||0)*Math.max(1,Number(part.q)||1),0);
        const analysisPartArea = Number(analysis.partArea);
        const usedArea = Number.isFinite(analysisPartArea) && analysisPartArea > 0 ? analysisPartArea : calculatedSheetUsedArea;
        const wasteArea = Math.max(0, stockArea - usedArea);
        const usedPct = stockArea > 0 ? usedArea / stockArea * 100 : 0;
        const wastePct = stockArea > 0 ? wasteArea / stockArea * 100 : 0;
        const analysisCutLength = Number(analysis.cutLength);
        const cutLength = Number.isFinite(analysisCutLength) && analysisCutLength >= 0
          ? analysisCutLength
          : (group.cuts || []).reduce((sum, cut) => sum + Math.hypot(Number(cut.x2||0)-Number(cut.x1||0), Number(cut.y2||0)-Number(cut.y1||0)),0);
        const analysisPanels = Number(analysis.totalParts);
        const panels = Number.isFinite(analysisPanels) && analysisPanels > 0
          ? analysisPanels
          : (group.parts || []).reduce((sum,part)=>sum+Math.max(1,Number(part.q)||1),0);
        const analysisCuts = Number(analysis.numberOfCuts);
        const sheetCutCount = Number.isFinite(analysisCuts) && analysisCuts >= 0 ? analysisCuts : (group.cuts || []).length;

        // Left tables: measured positions/row pitch from the supplied reference PDF.
        const rowStep = 9.6;
        const firstBaseline = blockTop - 4.4;
        const rows = [
          ['Stock sheet', `${clean(stockW)} x ${clean(stockL)}`],
          ['Used area', `${num(usedArea)} ${num(usedPct)}%`],
          ['Wasted area', `${num(wasteArea)} ${num(wastePct)}%`],
          ['Cuts', String(sheetCutCount)],
          ['Cut length', num(cutLength)],
          ['Total Pieces', String(panels)],
          ['Wasted panels', String((group.offcuts || []).length)],
        ];
        rows.forEach((row, i) => {
          const baseline = firstBaseline - i * rowStep;
          if (i === 0) rect(cmds, leftX, baseline - 2.8, leftW, 7.2, [0.85,0.85,0.85], null);
          text(cmds, row[0], leftX + 2, baseline, 5.25, true);
          fitText(cmds, row[1], leftX + 76, baseline, 15, 5.25, false);
          line(cmds, leftX, baseline - 3.5, leftX + leftW, baseline - 3.5, .18, [0.84,0.84,0.84]);
        });

        const panelHeaderY = blockTop - 78.5;
        rect(cmds, leftX, panelHeaderY - 2.8, leftW, 7.2, [0.85,0.85,0.85], null);
        text(cmds, 'Panel', leftX + 2, panelHeaderY, 5.25, true);
        text(cmds, 'Tolerance', leftX + 48, panelHeaderY, 4.8, true);
        text(cmds, 'Qty', leftX + 104, panelHeaderY, 5.25, true);
        sizes.forEach((item, index) => {
          const baseline = panelHeaderY - (index + 1) * rowStep;
          text(cmds, item.size.replace('x',' x '), leftX + 2, baseline, 5.0, false);
          fitText(cmds, item.tolerance, leftX + 48, baseline, 18, 4.35, false);
          text(cmds, String(item.qty), leftX + 104, baseline, 5.0, false);
        });

        const cutHeaderY = panelHeaderY - ((sizes.length + 1) * rowStep) - 8.5;
        rect(cmds, leftX, cutHeaderY - 2.8, leftW, 7.2, [0.85,0.85,0.85], null);
        text(cmds, '#', leftX + 2, cutHeaderY, 5.15, true);
        text(cmds, 'Panel', leftX + 14, cutHeaderY, 5.15, true);
        text(cmds, 'Cut', leftX + 54, cutHeaderY, 5.15, true);
        text(cmds, 'Result', leftX + 70, cutHeaderY, 5.15, true);
        cutRows.forEach((cutRow, index) => {
          const baseline = cutHeaderY - (index + 1) * rowStep;
          text(cmds, String(cutRow.number), leftX + 2, baseline, 4.85, false);
          fitText(cmds, cutRow.panel, leftX + 14, baseline, 13, 4.85, false);
          fitText(cmds, cutRow.cut, leftX + 54, baseline, 8, 4.85, false);
          fitText(cmds, cutRow.result, leftX + 70, baseline, 18, 4.85, false);
        });

        // Right sheet diagram. IMPORTANT: SmartCut's Y axis is bottom-up and PDF's
        // Y axis is also bottom-up, so coordinates must NOT be vertically mirrored.
        rect(cmds, layoutX, layoutY, layoutW, layoutH, [0.988,0.988,0.988], [0.22,0.22,0.22], .72);
        text(cmds, clean(stockL), layoutX + layoutW / 2 - 4, layoutY - 10.5, 5.4, false, [0.74,0.30,0.30]);
        text(cmds, clean(stockW), layoutX + layoutW + 7.0, layoutY + layoutH / 2, 5.4, false, [0.74,0.30,0.30]);
        line(cmds, layoutX, layoutY - 4.0, layoutX + layoutW, layoutY - 4.0, .35, [0.83,0.48,0.48]);
        line(cmds, layoutX + layoutW + 4.0, layoutY, layoutX + layoutW + 4.0, layoutY + layoutH, .35, [0.83,0.48,0.48]);

        (group.offcuts || []).forEach((offcut) => {
          const ox = Number(offcut.x || 0), oy = Number(offcut.y || 0);
          const ol = Number(offcut.l || 0), ow = Number(offcut.w || 0);
          if (!(ol > 0 && ow > 0)) return;
          const rx = layoutX + ox * scale;
          const ry = layoutY + oy * scale;
          const rw = ol * scale;
          const rh = ow * scale;
          rect(cmds, rx, ry, rw, rh, [0.975,0.975,0.975], [0.76,0.76,0.76], .32);
          if (rw > 18) text(cmds, clean(ol), rx + rw/2 - 3.5, ry + rh - 5.3, 4.0, false, [0.58,0.58,0.58]);
          if (rh > 15) textRot90(cmds, clean(ow), rx + 5.5, ry + rh/2 - 3.0, 4.0, false, [0.58,0.58,0.58]);
        });

        (group.parts || []).forEach((part) => {
          const x = Number(part.x || 0);
          const y = Number(part.y || 0);
          // SmartCut's placed result dimensions are already oriented exactly as
          // rendered by its diagram. Do not apply `rot` a second time.
          const pl = Number(part.l || 0);
          const pw = Number(part.w || 0);
          if (!(pl > 0 && pw > 0)) return;
          const rx = layoutX + x * scale;
          const ry = layoutY + y * scale;
          const rw = pl * scale;
          const rh = pw * scale;
          const key = this.getPartSizeKey(part);
          const color = sizeColorMap.get(key) || [0.9,0.9,0.9];
          rect(cmds, rx, ry, rw, rh, color, [0.20,0.20,0.20], .62);
          if (rw > 16) text(cmds, clean(pl), rx + rw/2 - 3.2, ry + rh - 5.4, 4.1, false, [0.24,0.24,0.24]);
          if (rh > 14) textRot90(cmds, clean(pw), rx + 5.2, ry + rh/2 - 3.0, 4.1, false, [0.24,0.24,0.24]);
        });
      };

      plannedPages.forEach((pagePlan, pageIndex) => {
        const cmds = [];
        if (pageIndex === 0) drawOverallSummary(cmds);
        else drawPageTitle(cmds);
        pagePlan.blocks.forEach((entry) => drawSheetBlock(cmds, entry));
        line(cmds, margin, 30.3, pageW - margin, 30.3, .35, [0.48,0.48,0.48]);
        text(cmds, dateText, margin, footerY, 5.1, false, [0.35,0.35,0.35]);
        text(cmds, `page ${pageIndex + 1}/${pageCount}`, pageW - margin - 31, footerY, 5.1, false, [0.35,0.35,0.35]);
        pageStreams.push(cmds.join('\n') + '\n');
      });

      // PDF object assembly.
      pdfObjects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
      const pageIds = [];
      pageStreams.forEach((stream, index) => {
        const pageId = 5 + index * 2;
        const contentId = pageId + 1;
        pageIds.push(pageId);
        pdfObjects[pageId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> /Contents ${contentId} 0 R >>`;
        pdfObjects[contentId] = `<< /Length ${stream.length} >>\nstream\n${stream}endstream`;
      });
      pdfObjects[2] = `<< /Type /Pages /Kids [${pageIds.map((id)=>`${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;
      pdfObjects[fontRegular] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';
      pdfObjects[fontBold] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>';

      const maxId = Math.max(...Object.keys(pdfObjects).map(Number));
      let pdf = '%PDF-1.4\n%Plasticare Cut List\n';
      const offsets = new Array(maxId + 1).fill(0);
      for (let id = 1; id <= maxId; id += 1) {
        if (!pdfObjects[id]) continue;
        offsets[id] = pdf.length;
        pdf += `${id} 0 obj\n${pdfObjects[id]}\nendobj\n`;
      }
      const xref = pdf.length;
      pdf += `xref\n0 ${maxId + 1}\n0000000000 65535 f \n`;
      for (let id = 1; id <= maxId; id += 1) pdf += `${String(offsets[id]).padStart(10,'0')} 00000 n \n`;
      pdf += `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
      return new Blob([pdf], { type: 'application/pdf' });
    }


    destroy() {
      this.unsubscribeVariant?.();
      if (this.cutMinimumCartHandler) window.removeEventListener('plasticare:cut-minimum-updated', this.cutMinimumCartHandler);
      this.resetProductionPdf();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((builder) => {
      if (builder.dataset.initialized === 'true') return;
      new SmartCutOriginalCalculatorV2(builder);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once:true });
  else initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
